/**
 * A playlist's reference video: the picture cut a curator attaches so that clients can audition
 * the playlist's tracks against it.
 *
 * STORED AS A PATH, SIGNED WHEN SHOWN (D12, F22)
 *   `playlists.search_metadata.videoPath` is the object's path in the private `media` bucket,
 *   `playlists/{owner's id}/{uuid}.{ext}` (see video-upload.ts). The share page gets the video
 *   signed for an hour by the backend's share-link resolve. The owner's own screens sign it with
 *   `signPlaylistVideo`, and only the owner's: once N1 is applied, only the uploader can sign it,
 *   so the app offers the video to no other signed-in viewer (`PlaylistTracks`' `isOwner`).
 *   Before this, `videoUrl` held a 7-day signed URL that nothing renewed, so every video stopped
 *   playing a week after it was attached. Both writers, `writePlaylistVideo` here and
 *   `SupabasePlaylistService.createPlaylist`, delete `videoUrl` when they set `videoPath`.
 *
 * READ THE WAY THE BACKEND READS IT
 *   `playlistVideoPath` applies the backend's rules (`stored_video_path` and `in_owner_folder` in
 *   services/playlist_video_service.py), so the owner sees a video exactly when their clients
 *   will. The rules:
 *   - A non-blank `videoPath` wins. If it is invalid, the legacy URL is not tried instead.
 *   - Otherwise a legacy `videoUrl` on this project's `media` bucket gives its path.
 *   - Either way the path must lie inside `playlists/{owner}/`.
 *
 * OR A YOUTUBE LINK, FROM SOL'S SAVE RESULTS (owner's "Link + lead embed", 2026-10-23)
 *   `search_metadata.videoLink` holds `https://www.youtube.com/watch?v={id}`, rebuilt from the id
 *   the app's one parser read (src/lib/youtube.ts), never the string Sol was given. Never the
 *   legacy `videoUrl`: the backend reads that as a media-bucket URL. Only `writePlaylistVideoLink`
 *   writes it. A playlist has ONE video: every writer removes the other keys, and if a hand-edited
 *   row has both, the file wins (`playlistVideoLink`), as in the backend's resolve. The share page
 *   gets `{provider, video_id}` from resolve, re-parsed there on every visit, and embeds it; a
 *   link can't be played against a track, so its rows offer no "play against video".
 *
 * THE REST OF search_metadata IS KEPT
 *   A playlist made from a search keeps its query, filters and analysis in the same object. A
 *   change therefore reads the row as its owner, merges, and writes the row back.
 *
 * THE FILE A PLAYLIST NO LONGER NAMES IS DELETED, BEST-EFFORT
 *   This runs after the row is saved, or deleted (`SupabasePlaylistService.deletePlaylist` hands
 *   the deleted row's metadata to `deletePlaylistVideoFiles`), and only inside the owner's folder.
 *   Each such file belongs to one playlist: every upload has its own uuid name, createPlaylist
 *   stores only a video it uploaded itself, and neither Duplicate nor saving a shared playlist
 *   copies `search_metadata`. No app flow makes a track video link from a playlist's file. The
 *   delete never fails the change. Until migration 20261012030000_media_owner_only_access.sql (N1)
 *   gives owners a DELETE policy on `media`, Storage deletes nothing and reports no error.
 */
import { supabase } from "@/lib/supabase";
import { uploadVideoToMedia } from "@/lib/video-upload";
import { canonicalYouTubeUrl, youTubeVideoId } from "@/lib/youtube";
import type { Json } from "@/types/database";

/** A playlist's `search_metadata` as stored: a JSON object, passed through except for the video. */
export type PlaylistSearchMetadata = Record<string, unknown>;

/** How long a URL signed for the owner's own screens lasts. Share pages get theirs from resolve. */
export const OWNER_VIDEO_URL_TTL_SECONDS = 60 * 60;

const MEDIA_BUCKET = "media";
const PLAYLIST_FOLDER = "playlists";
const MAX_PATH_LENGTH = 512;
const STORAGE_OBJECT_PATH = /^\/storage\/v1\/object\/(?:sign|public|authenticated)\/media\/(.+)$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\x00-\x1f\x7f]/;

/**
 * The `media` object path inside a stored Supabase Storage URL, or null. The URL must be https on
 * this project's own Supabase origin and name the `media` bucket. The signature and any other
 * query are ignored, and the path comes back decoded.
 */
export function mediaPathFromStorageUrl(url: string): string | null {
  try {
    const project = new URL(import.meta.env.VITE_SUPABASE_URL);
    const parsed = new URL(url.trim());
    if (
      parsed.protocol !== "https:" ||
      parsed.origin !== project.origin ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    const match = STORAGE_OBJECT_PATH.exec(parsed.pathname);
    return match ? decodeURIComponent(match[1]) || null : null;
  } catch {
    return null;
  }
}

/** True for a plain object path inside `playlists/{ownerId}/`: the backend's `in_owner_folder`. */
function isInOwnerFolder(path: string, ownerId: string | undefined): boolean {
  if (!ownerId) return false;
  // Counted in code points, as the backend counts them.
  if (Array.from(path).length > MAX_PATH_LENGTH) return false;
  if (CONTROL_CHARACTERS.test(path) || path.includes("\\")) return false;
  const segments = path.split("/");
  if (segments.length < 3) return false;
  if (segments.some((segment) => segment === "" || segment === "." || segment === "..")) {
    return false;
  }
  return segments[0] === PLAYLIST_FOLDER && segments[1] === ownerId;
}

type StoredVideoKeys = { videoPath?: unknown; videoUrl?: unknown; videoLink?: unknown };

/**
 * The file the row names, wherever it is: the backend's `stored_video_path`. A non-blank
 * `videoPath` wins; a `videoPath` that isn't a string names none, and the legacy URL isn't tried.
 */
function storedVideoPath(meta: StoredVideoKeys | null | undefined): string | null {
  if (!meta || typeof meta !== "object") return null;
  const { videoPath, videoUrl } = meta;
  if (typeof videoPath === "string" && videoPath.trim()) return videoPath;
  if (videoPath !== undefined && videoPath !== null && typeof videoPath !== "string") return null;
  return typeof videoUrl === "string" ? mediaPathFromStorageUrl(videoUrl) : null;
}

/**
 * The playlist's video as an object path in the `media` bucket, or null when it has none that its
 * share page would show. `ownerId` is the playlist's `user_id` (`Playlist.createdBy`).
 */
export function playlistVideoPath(
  meta: StoredVideoKeys | null | undefined,
  ownerId: string | undefined
): string | null {
  const path = storedVideoPath(meta);
  return path !== null && isInOwnerFolder(path, ownerId) ? path : null;
}

/**
 * The video id of the playlist's YouTube link (`videoLink`), or null: when it has none
 * `youTubeVideoId` accepts, or when the row names a file, which wins (the backend's
 * `reference_video_link`). The app stores only the canonical link, which the backend's stricter,
 * https-only check of stored values takes too. The two parsers differ only on the three forms
 * that only input takes (http://, no scheme, music.youtube.com), so only a row edited by hand
 * into one of those would show here and not on the share page.
 */
export function playlistVideoLink(meta: StoredVideoKeys | null | undefined): string | null {
  if (!meta || typeof meta !== "object" || storedVideoPath(meta) !== null) return null;
  return youTubeVideoId(meta.videoLink);
}

/**
 * A URL for the owner's own screens, valid for an hour, or null when the path can't be signed.
 * It is signed with the viewer's own session: once N1 (20261012030000_media_owner_only_access.sql)
 * is applied, that works for the playlist's owner, who uploaded the video, and nobody else.
 */
export async function signPlaylistVideo(path: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(MEDIA_BUCKET)
      .createSignedUrl(path, OWNER_VIDEO_URL_TTL_SECONDS);
    if (error || !data?.signedUrl) {
      console.warn("Couldn't sign the playlist's video:", error?.message ?? "no URL came back");
      return null;
    }
    return data.signedUrl;
  } catch (error) {
    console.warn("Couldn't sign the playlist's video:", error);
    return null;
  }
}

/**
 * Upload `file` as the playlist's video, replacing any video it had, and return the stored
 * `search_metadata`. Throws, before anything is uploaded, unless the signed-in user owns the
 * playlist. Also throws when the upload or the save fails.
 */
export async function writePlaylistVideo(
  playlistId: string,
  file: File
): Promise<PlaylistSearchMetadata> {
  const { ownerId, metadata } = await readOwnSearchMetadata(playlistId);
  const videoPath = await uploadVideoToMedia(PLAYLIST_FOLDER, file);

  const next: PlaylistSearchMetadata = { ...metadata, videoPath };
  delete next.videoUrl;
  delete next.videoLink;
  const stored = await saveSearchMetadata(playlistId, ownerId, next);

  await deleteVideoFiles(ownedVideoFiles(metadata, ownerId).filter((path) => path !== videoPath));
  return stored;
}

/**
 * Remove the playlist's video and return the stored `search_metadata`. Throws unless the signed-in
 * user owns the playlist, and when the save fails.
 */
export async function clearPlaylistVideo(playlistId: string): Promise<PlaylistSearchMetadata> {
  const { ownerId, metadata } = await readOwnSearchMetadata(playlistId);

  const next: PlaylistSearchMetadata = { ...metadata };
  delete next.videoPath;
  delete next.videoUrl;
  delete next.videoLink;
  const stored = await saveSearchMetadata(playlistId, ownerId, next);

  await deleteVideoFiles(ownedVideoFiles(metadata, ownerId));
  return stored;
}

/**
 * Store a YouTube link as the playlist's video, replacing any video it had, and return the stored
 * `search_metadata`. The link is stored canonical, rebuilt from its id. Throws, before reading
 * anything, for a link the parser refuses; then unless the signed-in user owns the playlist; and
 * when the save fails. Written by Sol's Save results only (owner, 2026-10-23).
 */
export async function writePlaylistVideoLink(
  playlistId: string,
  url: string
): Promise<PlaylistSearchMetadata> {
  const videoId = youTubeVideoId(url);
  if (!videoId) throw new Error("Only a YouTube video link can be saved with a playlist.");
  const { ownerId, metadata } = await readOwnSearchMetadata(playlistId);

  const next: PlaylistSearchMetadata = { ...metadata, videoLink: canonicalYouTubeUrl(videoId) };
  delete next.videoPath;
  delete next.videoUrl;
  const stored = await saveSearchMetadata(playlistId, ownerId, next);

  await deleteVideoFiles(ownedVideoFiles(metadata, ownerId));
  return stored;
}

/**
 * Delete the video files a deleted playlist named, given the `search_metadata` of the row that was
 * deleted and its owner. Best-effort, like the delete after a replace: only files inside
 * `playlists/{ownerId}/`, and a failure is logged, never thrown, because the playlist is gone.
 */
export async function deletePlaylistVideoFiles(
  searchMetadata: Json | null | undefined,
  ownerId: string
): Promise<void> {
  await deleteVideoFiles(ownedVideoFiles(asSearchMetadata(searchMetadata), ownerId));
}

function asSearchMetadata(value: Json | null | undefined): PlaylistSearchMetadata {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? { ...value } : {};
}

/** The playlist's `search_metadata`, read as its owner. Throws for anyone else. */
async function readOwnSearchMetadata(
  playlistId: string
): Promise<{ ownerId: string; metadata: PlaylistSearchMetadata }> {
  const { data: auth } = await supabase.auth.getUser();
  const user = auth?.user;
  if (!user) throw new Error("Sign in to change this playlist's video.");

  const { data, error } = await supabase
    .from("playlists")
    .select("search_metadata")
    .eq("id", playlistId)
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Only the playlist's owner can change its video.");
  return { ownerId: user.id, metadata: asSearchMetadata(data.search_metadata) };
}

async function saveSearchMetadata(
  playlistId: string,
  ownerId: string,
  metadata: PlaylistSearchMetadata
): Promise<PlaylistSearchMetadata> {
  const { data, error } = await supabase
    .from("playlists")
    // The row's own JSON with the video keys set or removed, so it is still JSON.
    .update({ search_metadata: metadata as Json })
    .eq("id", playlistId)
    .eq("user_id", ownerId)
    .select("search_metadata")
    .single();
  if (error) throw error;
  return asSearchMetadata(data.search_metadata);
}

/** Every file in the owner's folder that this metadata names, under either key. */
function ownedVideoFiles(metadata: PlaylistSearchMetadata, ownerId: string): string[] {
  const named = [
    typeof metadata.videoPath === "string" ? metadata.videoPath : null,
    typeof metadata.videoUrl === "string" ? mediaPathFromStorageUrl(metadata.videoUrl) : null,
  ];
  const owned = named.filter(
    (path): path is string => path !== null && isInOwnerFolder(path, ownerId)
  );
  return Array.from(new Set(owned));
}

/** Best-effort: a failure is logged, never thrown, because no playlist names these files now. */
async function deleteVideoFiles(paths: string[]): Promise<void> {
  if (paths.length === 0) return;
  try {
    const { error } = await supabase.storage.from(MEDIA_BUCKET).remove(paths);
    if (error) console.warn("Couldn't delete a playlist video file:", error.message);
  } catch (error) {
    console.warn("Couldn't delete a playlist video file:", error);
  }
}
